import { Capacitor } from '@capacitor/core';

import { getQueryParams } from '../utils';
import getStorage from '../utils/storage.utils';

/**
 * Read a first-party cookie set by Facebook Pixel (`_fbp` or `_fbc`).
 * Returns null on native platforms (Pixel doesn't run there) or when the
 * cookie is absent (e.g. ad blockers, ITP, Pixel hasn't loaded yet).
 */
const getPixelCookie = (name: '_fbp' | '_fbc'): string | null => {
  if (Capacitor.isNativePlatform() || typeof document === 'undefined') return null;
  const match = document.cookie.split('; ').find((row) => row.startsWith(`${name}=`));
  return match ? decodeURIComponent(match.split('=')[1]) : null;
};

// Add the Segment script to the head of the document
if (import.meta.env.VITE_SEGMENT?.length > 8) {
  const target = document.getElementsByTagName('head');
  const newScript = document.createElement('script');
  const inlineScript = document.createTextNode(`!(function () {
  var analytics = (window.analytics = window.analytics || [])
  if (!analytics.initialize)
    if (analytics.invoked)
      window.console &&
        console.error &&
        console.error('Segment snippet included twice.')
    else {
      analytics.invoked = !0
      analytics.methods = [
        'trackSubmit',
        'trackClick',
        'trackLink',
        'trackForm',
        'pageview',
        'identify',
        'reset',
        'group',
        'track',
        'ready',
        'alias',
        'debug',
        'page',
        'once',
        'off',
        'on',
        'addSourceMiddleware',
        'addIntegrationMiddleware',
        'setAnonymousId',
        'addDestinationMiddleware',
      ]
      analytics.factory = function (e) {
        return function () {
          var t = Array.prototype.slice.call(arguments)
          t.unshift(e)
          analytics.push(t)
          return analytics
        }
      }
      for (var e = 0; e < analytics.methods.length; e++) {
        var key = analytics.methods[e]
        analytics[key] = analytics.factory(key)
      }
      analytics.load = function (key, e) {
        var t = document.createElement('script')
        t.type = 'text/javascript'
        t.async = !0
        t.src =
          'https://cdn.segment.com/analytics.js/v1/' +
          key +
          '/analytics.min.js'
        var n = document.getElementsByTagName('script')[0]
        n.parentNode.insertBefore(t, n)
        analytics._loadOptions = e
      }
      analytics._writeKey = '${import.meta.env.VITE_SEGMENT}'
      analytics.SNIPPET_VERSION = '4.15.3'
      analytics.load('${import.meta.env.VITE_SEGMENT}')
    }
})()`);
  newScript.appendChild(inlineScript);
  target[0]?.appendChild(newScript);
}

interface IGAParams {
  utmCampaign?: string | null;
  utmSource?: string | null;
  utmContent?: string | null;
  utmMedium?: string | null;
  referrer?: string | null;
  gclid?: string | null;
  fbclid?: string | null;
  fbp?: string | null;
  fbc?: string | null;
  referralUid?: string | null;
  context: any;
}

const params: IGAParams = {
  context: {},
};

export const getUtmParams = async () => {
  const referrer = document.referrer;
  const queryParams = getQueryParams();
  const storage = getStorage();

  // Prioritize URL params over storage for all UTM parameters
  const utmCampaign = await storage.getItem('utm_campaign');
  if (queryParams['utm_campaign'] || utmCampaign) params.utmCampaign = queryParams['utm_campaign'] || utmCampaign;

  const utmSource = await storage.getItem('utm_source');
  if (queryParams['utm_source'] || utmSource) params.utmSource = queryParams['utm_source'] || utmSource;

  const utmContent = await storage.getItem('utm_content');
  if (queryParams['utm_content'] || utmContent) params.utmContent = queryParams['utm_content'] || utmContent;

  const utmMedium = await storage.getItem('utm_medium');
  if (queryParams['utm_medium'] || utmMedium) params.utmMedium = queryParams['utm_medium'] || utmMedium;

  const nextReferrer = await storage.getItem('referrer');
  if (nextReferrer || referrer) params.referrer = nextReferrer || referrer;

  // Check if gclid has expired (90 days = 7776000000 ms)
  const gclidTimestamp = await storage.getItem('gclid_timestamp');
  const isGclidExpired = gclidTimestamp && Date.now() - parseInt(gclidTimestamp) > 7776000000;

  // Clear expired gclid
  if (isGclidExpired) {
    await storage.removeItem('gclid');
    await storage.removeItem('gclid_timestamp');
  }

  // Persist gclid from URL and prioritize fresh gclid over storage
  if (queryParams['gclid']) {
    await storage.setItem('gclid', queryParams['gclid']);
    await storage.setItem('gclid_timestamp', Date.now().toString());
    params.gclid = queryParams['gclid'];
  } else {
    const nextGclid = !isGclidExpired ? await storage.getItem('gclid') : null;
    if (nextGclid) params.gclid = nextGclid;
  }

  // --- Facebook attribution ---
  //
  // Preference order on web: real Pixel cookie > storage > generate.
  // On native: storage > generate (Pixel cookies don't exist there).
  //
  // The real `_fbp`/`_fbc` cookies set by Facebook Pixel are higher quality
  // than self-generated values for Conversions API attribution, so we prefer
  // them whenever they're present and mirror them into storage so subsequent
  // calls stay consistent if cookies are later cleared.

  // fbclid: capture from URL with 90-day expiry (same as gclid)
  const fbclidTimestamp = await storage.getItem('fbclid_timestamp');
  const isFbclidExpired = fbclidTimestamp && Date.now() - parseInt(fbclidTimestamp) > 7776000000;

  if (isFbclidExpired) {
    await storage.removeItem('fbclid');
    await storage.removeItem('fbclid_timestamp');
    await storage.removeItem('fbc');
  }

  if (queryParams['fbclid']) {
    await storage.setItem('fbclid', queryParams['fbclid']);
    await storage.setItem('fbclid_timestamp', Date.now().toString());
    params.fbclid = queryParams['fbclid'];
  } else {
    const nextFbclid = !isFbclidExpired ? await storage.getItem('fbclid') : null;
    if (nextFbclid) params.fbclid = nextFbclid;
  }

  // fbc: prefer Pixel's `_fbc` cookie. If absent, construct from fbclid (fresh
  // or stored), then fall back to a previously stored fbc value.
  const pixelFbc = getPixelCookie('_fbc');
  if (pixelFbc) {
    params.fbc = pixelFbc;
    await storage.setItem('fbc', pixelFbc);
  } else if (queryParams['fbclid']) {
    const fbc = `fb.1.${Date.now()}.${queryParams['fbclid']}`;
    await storage.setItem('fbc', fbc);
    params.fbc = fbc;
  } else {
    const storedFbc = !isFbclidExpired ? await storage.getItem('fbc') : null;
    if (storedFbc) params.fbc = storedFbc;
  }

  // fbp: prefer Pixel's `_fbp` cookie. If absent, fall back to a previously
  // stored value, then generate one as a last resort.
  const pixelFbp = getPixelCookie('_fbp');
  if (pixelFbp) {
    params.fbp = pixelFbp;
    await storage.setItem('fbp', pixelFbp);
  } else {
    let fbp = await storage.getItem('fbp');
    if (!fbp) {
      const randomId = Math.floor(Math.random() * 10000000000)
        .toString()
        .padStart(10, '0');
      fbp = `fb.1.${Date.now()}.${randomId}`;
      await storage.setItem('fbp', fbp);
    }
    params.fbp = fbp;
  }

  const referralUid = await storage.getItem('referral_uid');
  if (referralUid) params.referralUid = referralUid;

  return params;
};

/**
 * Sprig's Segment destination is device-mode, so it only ever sees identify calls made from
 * the browser — the server-side identify in api-internal never reaches it. This call is the
 * only thing that ties an in-product survey response to a userId.
 *
 * Scoped to Sprig alone: Intercom is identified separately with an HMAC hash (see
 * user.utils.tsx) and Facebook Pixel would pick up `email` as advanced matching.
 */
export const segmentIdentify = async ({ firebaseUser, traits }) => {
  window.analytics &&
    window.analytics.identify &&
    window.analytics.identify(firebaseUser.uid, traits, {
      integrations: {
        All: false,
        // Must match the destination's creationName, not its display name in the UI.
        'Sprig (Actions)': true,
        // Without this analytics.js drops the call before it reaches Segment at all.
        'Segment.io': true,
      },
    });
};

let queue = [];

export const trackSegment = (name: Track, properties: SectionProperties) => {
  queue.push({ name, properties });

  if (window?.analytics?.track) {
    const currQueue = [...queue];
    queue = [];

    currQueue.forEach(({ name, properties }) => {
      window.analytics.track(name, properties);
    });
  }
};

setInterval(() => {
  if (queue.length && window?.analytics?.track) {
    const currQueue = [...queue];
    queue = [];

    currQueue.forEach(({ name, properties }) => {
      window.analytics.track(name, properties);
    });
  }
}, 1000);
